import classNames from 'classnames';
import { PropsWithChildren } from 'react';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

const cardPosition = keyframes`
  0% {
    transform: translateY(0px);
  }
  30% {
    transform: translateY(-25px);
  }
  100% {
    transform: translateY(0px);
  }
`;
const cardGlow = keyframes`
  0% {
    opacity: 0;
    backdrop-filter: drop-shadow(1px 1px 0px transparent);;
  }
  45% {
    opacity: 1;
    backdrop-filter: drop-shadow(1px 1px 20px var(--c-rivals-friendlies));
  }
  100% {
    opacity: 0;
    backdrop-filter: drop-shadow(1px 1px 0px transparent);;
  }
`;
const bigBoost = keyframes`
  0% {
    opacity: 0;
    top: 90%;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    top: 20%;
  }
`;
const mediumBoost = keyframes`
  0% {
    opacity: 0;
    top: 80%;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    top: 15%;
  }
`;
const mediumBoost2 = keyframes`
  0% {
    opacity: 0;
    top: 75%;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    top: 10%;
  }
`;
const Root = styled(Vertical)`
  --delay: 0ms;

  isolation: isolate;
  position: relative;

  &.animate {
    animation: 1.7s ${cardPosition} var(--delay) cubic-bezier(1, 0, 0.67, 1);
  }
`;
const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;

  opacity: 0;
  border-radius: var(--double-unit);
  background: linear-gradient(
    to bottom,
    transparent 10%,
    var(--c-rivals-friendlies)
  );

  .animate & {
    animation: 1400ms ${cardGlow} var(--delay) cubic-bezier(1, 0, 0.67, 1)
      forwards;
  }
`;
const AnimatedBoostWrapper = styled.div`
  position: absolute;
  opacity: 0;
`;
const BigAnimatedBoostWrapper = styled(AnimatedBoostWrapper)`
  left: 50%;
  transform: translateX(-50%);
  bottom: -50px;

  .animate & {
    animation: 500ms ${bigBoost} calc(var(--delay) + 400ms)
      cubic-bezier(0.19, -0, 0.23, 1) forwards;
  }
`;
const MediumAnimatedBoostWrapper = styled(AnimatedBoostWrapper)`
  left: -2%;
  bottom: 0;

  .animate & {
    animation: 500ms ${mediumBoost} calc(var(--delay) + 600ms)
      cubic-bezier(0.19, -0, 0.23, 1) forwards;
  }
`;
const MediumAnimatedBoostWrapper2 = styled(AnimatedBoostWrapper)`
  right: -1%;
  bottom: 0;

  .animate & {
    animation: 500ms ${mediumBoost2} calc(var(--delay) + 800ms)
      cubic-bezier(0.19, -0, 0.23, 1) forwards;
  }
`;
const BigBoost = styled(Boost)`
  height: 100px !important;
`;
const MediumBoost = styled(Boost)`
  height: 70px !important;
`;

type Props = { animate?: boolean; delay?: number };
export const LevelUpAnimation = ({
  animate,
  children,
  delay = 0,
}: PropsWithChildren<Props>) => {
  return (
    <Root
      className={classNames({ animate })}
      style={{ '--delay': `${delay}ms` } as CSSProperties}
    >
      <CardGlow />
      <BigAnimatedBoostWrapper>
        <BigBoost color="var(--c-rivals-friendlies)" />
      </BigAnimatedBoostWrapper>
      <MediumAnimatedBoostWrapper>
        <MediumBoost color="var(--c-rivals-friendlies)" />
      </MediumAnimatedBoostWrapper>
      <MediumAnimatedBoostWrapper2>
        <MediumBoost color="var(--c-rivals-friendlies)" />
      </MediumAnimatedBoostWrapper2>
      {children}
    </Root>
  );
};
